// Shared building blocks for the conversation pages (ticket, dev ticket) — redesign
// of 2026-10-23. Everything is coloured through the theme tokens (theme/animations.jsx),
// so all six brand themes in both modes re-skin it; nothing here names a colour of
// one theme. Hover/focus states need real CSS, so the look lives in CONVO_CSS
// (classes prefixed cv-) and is mounted once per page with <ConvoStyles/>.
import { useState, useEffect, useLayoutEffect, useRef, useMemo, memo } from "react";
import { ChevronDown, ChevronUp, Paperclip, FileText, ArrowDown, Reply } from "lucide-react";
import { HtmlContent, looksLikeHtml } from "../HtmlContent";
import { initialsOf } from "../../theme";
import { fmtFileSize } from "../../utils/pastedFiles";

export const CONVO_CSS = `
.cv-root{font-family:var(--t-font,inherit);color:var(--t-text-body)}
.cv-root h1{font-family:var(--t-font-head,inherit);color:var(--t-text-primary);font-weight:700;margin:0}
.cv-btn{font-family:var(--t-font-btn,inherit);font-weight:var(--t-btn-weight,600);text-transform:var(--t-btn-transform,none);letter-spacing:var(--t-btn-letter,0);font-size:11.5px;height:36px;padding:0 14px;display:inline-flex;align-items:center;justify-content:center;gap:7px;border-radius:var(--t-radius-sm,8px);border:1px solid var(--t-border-soft);background:var(--t-gloss,none),var(--t-input);color:var(--t-text-body);cursor:pointer;white-space:nowrap;text-decoration:none;transition:border-color .15s,color .15s,background-color .15s,filter .15s}
.cv-btn:hover:not(:disabled){border-color:var(--t-border-mid);color:var(--t-text-primary)}
.cv-btn:disabled{opacity:.5;cursor:not-allowed}
.cv-btn-primary{background:var(--t-accent-grad);color:#fff;border-color:transparent;box-shadow:var(--t-btn-shadow,var(--t-accent-glow))}
.cv-btn-primary:hover:not(:disabled){color:#fff;border-color:transparent;filter:brightness(1.07)}
.cv-btn-icon{width:36px;padding:0}
.cv-btn-ghost{background:transparent;border-color:transparent;box-shadow:none}
.cv-btn-ghost:hover:not(:disabled){background:var(--t-accent-softer);border-color:transparent}
.cv-btn-on{border-color:var(--t-accent);color:var(--t-text-primary);background:var(--t-accent-softer)}
.cv-btn-danger{color:var(--badge-red)}
.cv-chip{height:34px;padding:0 14px;border-radius:999px;border:1px solid var(--t-border-softer);background:transparent;color:var(--t-text-muted);font-family:inherit;font-size:13px;font-weight:600;display:inline-flex;align-items:center;gap:8px;cursor:pointer;white-space:nowrap}
.cv-chip:hover{border-color:var(--t-border-mid);color:var(--t-text-primary)}
.cv-chip.on{background:var(--t-accent-soft);border-color:var(--t-accent);color:var(--t-text-primary)}
.cv-chip-note.on{background:var(--t-note-bg);border-color:var(--t-note-bdr);color:var(--badge-amber)}
.cv-chip .cv-count{opacity:.7;font-weight:600}
.cv-msg{border:1px solid var(--t-border-softer);border-radius:var(--t-radius-lg,16px);background:var(--t-gloss,none),var(--t-panel);box-shadow:var(--t-shadow-panel);scroll-margin:16px}
.cv-msg-agent{background:linear-gradient(var(--t-accent-softer),var(--t-accent-softer)),var(--t-panel);border-color:var(--t-accent-soft)}
.cv-msg-ai{background:linear-gradient(rgba(139,92,246,.09),rgba(139,92,246,.09)),var(--t-panel);border-color:rgba(139,92,246,.34)}
.cv-msg-note{background:var(--t-note-bg);border-color:var(--t-note-bdr)}
.cv-row{width:100%;display:flex;align-items:center;gap:14px;padding:10px 18px;min-height:56px;border-radius:var(--t-radius-md,12px);border:1px solid var(--t-border-softer);background:var(--t-gloss,none),var(--t-panel);font-family:inherit;font-size:14px;color:inherit;text-align:left;cursor:pointer;scroll-margin:16px}
.cv-row:hover{border-color:var(--t-border-mid)}
.cv-row-note{background:var(--t-note-bg);border-color:var(--t-note-bdr)}
.cv-focus{outline:2px solid var(--t-accent);outline-offset:2px}
.cv-tag{font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;padding:3px 9px;border-radius:999px;border:1px solid var(--t-border-soft);white-space:nowrap;color:var(--t-text-body);line-height:1.3}
.cv-tag-tech{border-color:var(--t-accent-soft);background:var(--t-accent-softer)}
.cv-tag-ai{border-color:rgba(139,92,246,.4);background:rgba(139,92,246,.10)}
.cv-tag-note{color:var(--badge-amber);border-color:var(--t-note-bdr)}
.cv-tag-cc{color:var(--badge-blue);border-color:rgba(96,165,250,.40);background:rgba(96,165,250,.10);text-transform:none;letter-spacing:.02em}
.cv-tag-quiet{color:var(--t-text-faint);border-color:var(--t-border-softer);text-transform:none;letter-spacing:0;font-weight:600}
.cv-event{display:flex;align-items:center;gap:14px;font-size:12px;color:var(--t-text-faint);padding:4px 0;text-align:center}
.cv-event::before,.cv-event::after{content:"";flex:1;height:1px;background:var(--t-border-softer);min-width:24px}
.cv-sep{display:flex;align-items:center;gap:14px;font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--t-text-label);margin:14px 0 2px}
.cv-sep::after{content:"";flex:1;height:1px;background:var(--t-border-soft)}
.cv-input{background:var(--t-input);border:1px solid var(--t-border-soft);border-radius:var(--t-radius-md,12px);color:var(--t-text-primary);font-family:inherit}
textarea.cv-input:focus,.cv-input:focus-within{border-color:var(--t-accent);box-shadow:0 0 0 3px var(--t-accent-softer);outline:none}
.cv-grow{position:relative;display:flex;flex-direction:column}
.cv-topresize{position:absolute;left:0;right:0;top:-6px;height:12px;cursor:ns-resize;z-index:2;touch-action:none;outline:none}
.cv-topresize::after{content:"";position:absolute;left:50%;top:4px;width:40px;height:4px;margin-left:-20px;border-radius:99px;background:var(--t-accent);opacity:0;transition:opacity .15s}
.cv-topresize:hover::after,.cv-topresize:focus-visible::after,.cv-topresize.on::after{opacity:.85}
.cv-link{background:none;border:0;padding:0;color:var(--t-accent);font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;text-decoration:none}
.cv-link:hover{text-decoration:underline}
.cv-sect{border-bottom:1px solid var(--t-border-softer);padding:20px 22px;display:flex;flex-direction:column;gap:12px}
.cv-lbl{font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--t-text-label)}
.cv-kv{display:flex;justify-content:space-between;align-items:baseline;gap:12px;font-size:13px}
.cv-kv > :first-child{color:var(--t-text-faint)}
.cv-attach{display:inline-flex;align-items:center;gap:9px;height:38px;padding:0 12px;border-radius:var(--t-radius-sm,8px);border:1px solid var(--t-border-soft);background:var(--t-input);font-size:13px;color:var(--t-text-body);text-decoration:none;max-width:100%}
.cv-attach:hover{border-color:var(--t-border-mid);color:var(--t-text-primary)}
.cv-kbd{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11px;padding:2px 6px;border-radius:4px;border:1px solid var(--t-border-soft);background:var(--t-input);color:var(--t-text-muted)}
.cv-bar{height:7px;border-radius:999px;background:var(--t-surface-mid);overflow:hidden}
.cv-field{position:relative;display:flex;width:100%;min-width:0}
.cv-field select{appearance:none;-webkit-appearance:none;width:100%;min-width:0;height:38px;border-radius:var(--t-radius-sm,8px);border:1px solid var(--t-border-soft);background:var(--t-input);color:var(--t-text-primary);padding:0 32px 0 12px;font-family:inherit;font-size:13px;cursor:pointer;text-overflow:ellipsis}
.cv-field select:focus{border-color:var(--t-accent);outline:none;box-shadow:0 0 0 3px var(--t-accent-softer)}
.cv-field select:disabled{opacity:.6;cursor:wait}
.cv-field > svg{position:absolute;right:10px;top:50%;transform:translateY(-50%);pointer-events:none;color:var(--t-text-faint)}
.cv-pillsel{position:relative;display:inline-flex;border-radius:999px}
.cv-pillsel select{appearance:none;-webkit-appearance:none;height:30px;border-radius:999px;padding:0 28px 0 12px;font-family:inherit;font-size:12px;font-weight:700;border-width:1px;border-style:solid;cursor:pointer}
.cv-pillsel select:focus{outline:2px solid var(--t-accent);outline-offset:2px}
.cv-pillsel > svg{position:absolute;right:9px;top:50%;transform:translateY(-50%);pointer-events:none}
.cv-pill{display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 12px;border-radius:999px;font-size:12px;font-weight:700;border:1px solid;white-space:nowrap}
.cv-c-blue{color:var(--badge-blue);background:rgba(96,165,250,.14);border-color:rgba(96,165,250,.38)}
.cv-c-amber{color:var(--badge-amber);background:rgba(234,179,8,.14);border-color:rgba(234,179,8,.38)}
.cv-c-orange{color:var(--badge-orange);background:rgba(249,115,22,.13);border-color:rgba(249,115,22,.38)}
.cv-c-green{color:var(--badge-green);background:rgba(52,211,153,.13);border-color:rgba(52,211,153,.36)}
.cv-c-red{color:var(--badge-red);background:rgba(239,68,68,.13);border-color:rgba(239,68,68,.38)}
.cv-c-violet{color:var(--badge-violet);background:rgba(139,92,246,.14);border-color:rgba(139,92,246,.38)}
.cv-c-gray{color:var(--badge-gray);background:rgba(148,163,184,.12);border-color:rgba(148,163,184,.32)}
.cv-pillsel select option{color:var(--t-text-primary);background:var(--t-panel)}
.cv-compose{background:var(--t-compose-bg);border-top:1px solid var(--t-border-soft);backdrop-filter:var(--t-backdrop,none)}
.cv-pop{position:absolute;z-index:60;background:var(--t-panel);border:1px solid var(--t-border-soft);border-radius:var(--t-radius-md,12px);box-shadow:0 16px 40px rgba(0,0,0,.35);padding:10px}
.cv-menuitem{display:flex;align-items:center;gap:10px;width:100%;padding:9px 10px;border-radius:var(--t-radius-sm,8px);border:0;background:transparent;color:var(--t-text-body);font-family:inherit;font-size:13px;text-align:left;cursor:pointer;text-decoration:none}
.cv-menuitem:hover{background:var(--t-accent-softer);color:var(--t-text-primary)}
.cv-av{border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-weight:700;color:#fff;flex-shrink:0;font-family:inherit}
.cv-av-client{background:linear-gradient(135deg,#64748b,#8b9bb4)}
.cv-av-tech{background:var(--t-accent-grad)}
.cv-av-ai{background:linear-gradient(135deg,#7c3aed,#b57cf5)}
.cv-av-sys{background:var(--t-surface-mid);color:var(--t-text-muted)}
.cv-body{font-size:14px;line-height:1.7;color:var(--t-text-body);overflow-wrap:anywhere}
.cv-clamped{max-height:460px;overflow:hidden;-webkit-mask-image:linear-gradient(#000 78%,transparent);mask-image:linear-gradient(#000 78%,transparent)}
.cv-quoted{border-left:3px solid var(--t-border-mid);padding-left:14px;margin-top:4px;opacity:.92}
.cv-scroll{scroll-behavior:auto}
.cv-ccchip{display:inline-flex;align-items:center;gap:6px;max-width:100%;padding:3px 6px 3px 10px;border-radius:999px;border:1px solid var(--t-border-soft);background:var(--t-input);font-size:12px;color:var(--t-text-body)}
.cv-ccchip-new{border-style:dashed;border-color:var(--t-accent)}
.cv-ccchip button{background:none;border:0;padding:0 2px;color:var(--t-text-faint);cursor:pointer;display:inline-flex}
.cv-ccchip button:hover{color:var(--badge-red)}
.cv-progress{height:4px;border-radius:99px;background:var(--t-surface-mid);overflow:hidden}
.cv-progress > div{height:100%;background:var(--t-accent-grad);transition:width .2s}
@media (max-width:768px){.cv-hide-sm{display:none !important}.cv-sect{padding:16px}.cv-row{padding:10px 12px;gap:10px}.cv-cardbody{padding:14px 16px 16px !important}.cv-msg > header{flex-wrap:wrap;gap:10px !important;padding:12px 12px 12px 14px !important}}
`;

export function ConvoStyles() {
  return <style>{CONVO_CSS}</style>;
}

// Drag the TOP edge of a reply box up to make it taller (Juwan 2026-10-27). The browser's own
// handle only grows the box downwards from the bottom corner, and the composer sits at the
// bottom of the screen. Wrap the textarea in <div className="cv-grow"> with this beside it.
// Mouse, touch and the arrow keys (when the edge has focus) all work.
export function TopResizeHandle({ targetRef, min = 100 }) {
  const drag = useRef(null);
  const [on, setOn] = useState(false);
  const setHeight = (h) => {
    const el = targetRef.current;
    if (el) el.style.height = `${Math.round(Math.max(min, Math.min(window.innerHeight * 0.75, h)))}px`;
  };
  const end = (e) => { drag.current = null; setOn(false); try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not captured */ } };
  return (
    <div role="separator" aria-orientation="horizontal" aria-label="Resize the message box: drag up or down, or use the arrow keys"
      title="Drag to resize" tabIndex={0} className={`cv-topresize${on ? " on" : ""}`}
      onPointerDown={e => {
        const el = targetRef.current;
        if (!el || e.button > 0) return;
        e.preventDefault();
        drag.current = { y: e.clientY, h: el.getBoundingClientRect().height };
        e.currentTarget.setPointerCapture(e.pointerId);
        setOn(true);
      }}
      onPointerMove={e => { if (drag.current) setHeight(drag.current.h + (drag.current.y - e.clientY)); }}
      onPointerUp={end} onPointerCancel={end}
      onKeyDown={e => {
        const el = targetRef.current;
        if (!el || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
        e.preventDefault();
        setHeight(el.getBoundingClientRect().height + (e.key === "ArrowUp" ? 40 : -40));
      }} />
  );
}

// ── Text helpers ──────────────────────────────────────────────────────────────
export function stripHtml(s) {
  return String(s || "")
    .replace(/<(style|script)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|tr|h\d)>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}
export function previewText(s, n = 160) {
  const t = stripHtml(s).replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

// Where the quoted history of an email reply begins (Outlook, Gmail, Apple Mail,
// Yahoo, "-----Original Message-----", "On … wrote:"). Earliest marker wins.
const QUOTE_MARKERS_HTML = [
  /<div[^>]*\bid=["']?appendonsend\b/i,
  /<div[^>]*\bid=["']?divRplyFwdMsg\b/i,
  /<hr[^>]*\bid=["']?stopSpelling\b/i,
  /<hr[^>]*style=["'][^"']*display:\s*inline-block[^"']*width:\s*98%/i,
  /<div[^>]*\bclass=["'][^"']*\bgmail_quote\b/i,
  /<blockquote[^>]*\btype=["']?cite\b/i,
  /<div[^>]*\bclass=["'][^"']*\byahoo_quoted\b/i,
  /<div[^>]*\bid=["']?mail-editor-reference-message-container\b/i,
  /-{3,}\s*Original Message\s*-{3,}/i,
  /(?:<[^>]+>\s*)*On\s[^<]{6,200}?\swrote:\s*(?:<\/?[^>]+>\s*)*<blockquote/i,
  /<(?:p|div)[^>]*>\s*(?:<[^>]+>\s*)*From:\s*(?:<\/?[^>]+>\s*)*[^<]{1,200}(?:<\/?[^>]+>\s*)*(?:Sent|Date):/i,
];
const QUOTE_MARKERS_TEXT = [
  /\n-{3,}\s*Original Message\s*-{3,}/i,
  /\nOn\s.{6,200}?\swrote:\s*\n/,
  /\nFrom:\s.+\n(?:Sent|Date):\s/i,
  /\n>[^\n]*\n>/,
];
// Where a signature begins: ours (TicketDetail appends it after this exact <hr>),
// Outlook, Gmail, Thunderbird, and the plain-text "-- " convention.
const SIG_MARKERS_HTML = [
  /<hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0">/i,
  /<div[^>]*\bid=["']?Signature\b/i,
  /<div[^>]*\bclass=["'][^"']*\bgmail_signature\b/i,
  /<div[^>]*\bclass=["'][^"']*\bmoz-signature\b/i,
];
const SIG_MARKERS_TEXT = [/\n-- \n/];

function firstIndex(s, patterns) {
  let at = -1;
  for (const re of patterns) {
    const m = re.exec(s);
    if (m && (at === -1 || m.index < at)) at = m.index;
  }
  return at;
}
// Split a message into what was newly written, the signature, and the quoted
// history — so the thread shows the new words and folds the rest away. If the
// split would leave no new words (a bare forward), nothing is folded.
export function splitBody(body) {
  const s = String(body || "");
  if (!s) return { main: "", sig: "", quoted: "" };
  const isHtml = looksLikeHtml(s);
  const qAt = firstIndex(s, isHtml ? QUOTE_MARKERS_HTML : QUOTE_MARKERS_TEXT);
  let head = qAt > 0 ? s.slice(0, qAt) : s;
  const quoted = qAt > 0 ? s.slice(qAt) : "";
  const sAt = firstIndex(head, isHtml ? SIG_MARKERS_HTML : SIG_MARKERS_TEXT);
  const sig = sAt > 0 ? head.slice(sAt) : "";
  if (sAt > 0) head = head.slice(0, sAt);
  if (!stripHtml(head).trim()) return { main: s, sig: "", quoted: "" };
  return { main: head, sig, quoted };
}

// ── Dates ─────────────────────────────────────────────────────────────────────
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export function dayLabel(d) {
  const x = new Date(d); const now = new Date();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (sameDay(x, now)) return "Today";
  if (sameDay(x, y)) return "Yesterday";
  return x.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", ...(x.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) });
}
export function fmtWhen(d) {
  if (!d) return "";
  const x = new Date(d); const now = new Date();
  const time = x.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (sameDay(x, now)) return `Today, ${time}`;
  return `${x.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(x.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) })}, ${time}`;
}
export function fmtFull(d) {
  return d ? new Date(d).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";
}

// True while focus is in something you type into — single-key shortcuts must not fire.
export function isTypingTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
}

// ── Pieces ────────────────────────────────────────────────────────────────────
const AV_CLASS = { client: "cv-av-client", agent: "cv-av-tech", tech: "cv-av-tech", note: "cv-av-tech", ai: "cv-av-ai", activity: "cv-av-sys", dev: "cv-av-tech" };
export function Avatar({ name, kind = "client", size = 34, label }) {
  return (
    <span className={`cv-av ${AV_CLASS[kind] || "cv-av-client"}`} aria-hidden={label ? undefined : "true"} title={label}
      style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.36)) }}>
      {kind === "ai" ? "AI" : initialsOf(name)}
    </span>
  );
}

const TAG_CLASS = { client: "", tech: "cv-tag-tech", agent: "cv-tag-tech", ai: "cv-tag-ai", note: "cv-tag-note", cc: "cv-tag-cc", quiet: "cv-tag-quiet", activity: "cv-tag-quiet" };
export function Tag({ kind, children }) {
  return <span className={`cv-tag ${TAG_CLASS[kind] || ""}`}>{children}</span>;
}

export function Sel({ value, onChange, children, label, disabled, style }) {
  return (
    <span className="cv-field" style={style}>
      <select value={value} onChange={onChange} aria-label={label} disabled={disabled}>{children}</select>
      <ChevronDown size={14} aria-hidden="true" />
    </span>
  );
}

export const STATUS_TONE   = { "Open": "blue", "In Progress": "amber", "On Hold": "orange", "Resolved": "green", "Closed": "gray" };
export const PRIORITY_TONE = { Critical: "red", High: "orange", Medium: "amber", Low: "green" };
export function PillSel({ value, onChange, options, tone = "gray", label, disabled }) {
  return (
    <span className="cv-pillsel" style={{ color: `var(--badge-${tone})` }}>
      <select className={`cv-c-${tone}`} value={value} onChange={onChange} aria-label={label} disabled={disabled}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown size={12} aria-hidden="true" />
    </span>
  );
}

// Filter chips with counts.
export function Chips({ items, value, onChange, label = "Show messages" }) {
  return (
    <div role="group" aria-label={label} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {items.map(c => (
        <button key={c.key} type="button" className={`cv-chip${value === c.key ? " on" : ""}`} aria-pressed={value === c.key} onClick={() => onChange(c.key)}>
          {c.label}<span className="cv-count">{c.count}</span>
        </button>
      ))}
    </div>
  );
}

// Very long messages are clamped with a fade; "Show the full message" opens them.
export function Clamp({ children, resetKey }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const [open, setOpen] = useState(false);
  const measure = () => { const el = ref.current; if (el) setOver(el.scrollHeight > 520); };
  useLayoutEffect(() => { setOpen(false); measure(); }, [resetKey]);
  return (
    <div>
      <div ref={ref} className={over && !open ? "cv-clamped" : undefined} onLoadCapture={measure}>{children}</div>
      {over && (
        <button type="button" className="cv-link" style={{ marginTop: 8 }} onClick={() => setOpen(o => !o)}>
          {open ? "Show less" : "Show the full message"}
        </button>
      )}
    </div>
  );
}

const apiBase = () => process.env.REACT_APP_API_URL || "";
export function Attachments({ files }) {
  if (!files?.length) return null;
  const imgs = files.filter(a => a.mimetype?.startsWith("image/"));
  const docs = files.filter(a => !a.mimetype?.startsWith("image/"));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {imgs.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {imgs.map((a, i) => (
            <a key={`i${i}`} href={`${apiBase()}${a.url}`} target="_blank" rel="noreferrer" title={a.name}
              style={{ display: "block", maxWidth: "100%", borderRadius: "var(--t-radius-sm,8px)", overflow: "hidden", border: "1px solid var(--t-border-soft)" }}>
              <img src={`${apiBase()}${a.url}`} alt={a.name || "Attached image"} loading="lazy"
                style={{ display: "block", maxWidth: "100%", maxHeight: 320 }}
                onError={e => { e.currentTarget.parentElement.style.display = "none"; }} />
            </a>
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {docs.map((a, i) => (
            <a key={`d${i}`} className="cv-attach" href={`${apiBase()}${a.url}`} target="_blank" rel="noreferrer">
              <FileText size={15} aria-hidden="true" />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{a.name}</span>
              {a.size ? <span style={{ color: "var(--t-text-faint)", fontSize: 12 }}>{fmtFileSize(a.size)}</span> : null}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// The body of one message: new words, then (folded) signature and quoted history.
export function MessageBody({ body, parsed, renderQuoted }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const [showSig, setShowSig] = useState(false);
  const split = useMemo(() => (parsed ? null : splitBody(body)), [parsed, body]);
  if (parsed) {
    return (
      <div className="cv-body">
        <Clamp resetKey={parsed.reply}>
          {parsed.reply ? <HtmlContent text={parsed.reply} /> : <span style={{ fontStyle: "italic", color: "var(--t-text-faint)" }}>Replied with no new text.</span>}
        </Clamp>
        {renderQuoted ? renderQuoted(parsed.quoted) : null}
      </div>
    );
  }
  const { main, sig, quoted } = split;
  return (
    <div className="cv-body">
      <Clamp resetKey={main}>{main ? <HtmlContent text={main} /> : null}</Clamp>
      {(sig || quoted) && (
        <div style={{ display: "flex", gap: 20, marginTop: 12, flexWrap: "wrap" }}>
          {quoted && <button type="button" className="cv-link" aria-expanded={showQuoted} onClick={() => setShowQuoted(o => !o)}>{showQuoted ? "Hide quoted text" : "Show quoted text"}</button>}
          {sig && <button type="button" className="cv-link" aria-expanded={showSig} onClick={() => setShowSig(o => !o)}>{showSig ? "Hide signature" : "Show signature"}</button>}
        </div>
      )}
      {showSig && sig && <div style={{ marginTop: 10 }}><HtmlContent text={sig} /></div>}
      {showQuoted && quoted && <div className="cv-quoted" style={{ marginTop: 10 }}><HtmlContent text={quoted} /></div>}
    </div>
  );
}

const ROW_CLASS = { note: "cv-row cv-row-note", activity: "cv-row cv-row-note" };
const CARD_CLASS = { agent: "cv-msg cv-msg-agent", tech: "cv-msg cv-msg-agent", ai: "cv-msg cv-msg-ai", note: "cv-msg cv-msg-note", activity: "cv-msg cv-msg-note" };

// One collapsed message: a single line you can scan, click to open.
export function MessageRow({ e, focused, onToggle, innerRef }) {
  return (
    <button ref={innerRef} type="button" className={`${ROW_CLASS[e.kind] || "cv-row"}${focused ? " cv-focus" : ""}`} onClick={onToggle}
      aria-expanded="false" aria-label={`Open message from ${e.name}, ${fmtWhen(e.at)}`}>
      <Avatar name={e.name} kind={e.kind} />
      <span style={{ fontWeight: 700, color: "var(--t-text-primary)", whiteSpace: "nowrap" }}>{e.name}</span>
      <Tag kind={e.tagKind}>{e.role}</Tag>
      {(e.badges || []).slice(0, 1).map(b => <span key={b} className="cv-hide-sm"><Tag kind={b === "CC'd" ? "cc" : "quiet"}>{b}</Tag></span>)}
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--t-text-muted)" }}>{e.preview}</span>
      {e.files?.length > 0 && <Paperclip size={15} aria-label="Has attachments" style={{ color: "var(--t-text-faint)", flexShrink: 0 }} />}
      <span className="cv-hide-sm" style={{ color: "var(--t-text-faint)", fontSize: 12, whiteSpace: "nowrap" }}>{fmtWhen(e.at)}</span>
      <ChevronDown size={15} aria-hidden="true" style={{ color: "var(--t-text-faint)", flexShrink: 0 }} />
    </button>
  );
}

// One open message.
export function MessageCard({ e, focused, onToggle, onReply, innerRef, renderQuoted }) {
  return (
    <article ref={innerRef} tabIndex={-1} className={`${CARD_CLASS[e.kind] || "cv-msg"}${focused ? " cv-focus" : ""}`} aria-label={`${e.role} from ${e.name}`}>
      <header style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 14px 13px 18px", borderBottom: "1px solid var(--t-border-softer)" }}>
        <Avatar name={e.name} kind={e.kind} />
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: "1 1 220px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <strong style={{ color: "var(--t-text-primary)" }}>{e.name}</strong>
            <Tag kind={e.tagKind}>{e.role}</Tag>
            {(e.badges || []).map(b => <Tag key={b} kind={b === "CC'd" ? "cc" : "quiet"}>{b}</Tag>)}
          </div>
          {e.to && <span style={{ fontSize: 12, color: "var(--t-text-faint)", overflowWrap: "anywhere" }}>{e.to}</span>}
        </div>
        <span title={fmtFull(e.at)} style={{ color: "var(--t-text-faint)", fontSize: 12, whiteSpace: "nowrap" }}>{fmtWhen(e.at)}</span>
        {onReply && (
          <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Reply to this message" title="Reply" onClick={onReply}>
            <Reply size={16} />
          </button>
        )}
        <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Collapse this message" title="Collapse" aria-expanded="true" onClick={onToggle}>
          <ChevronUp size={16} />
        </button>
      </header>
      <div style={{ padding: "16px 22px 18px 66px", display: "flex", flexDirection: "column", gap: 14 }} className="cv-cardbody">
        <MessageBody body={e.body} parsed={e.parsed} renderQuoted={renderQuoted} />
        <Attachments files={e.files} />
      </div>
    </article>
  );
}

export function EventLine({ text, at }) {
  return <div className="cv-event"><span>{text}{at ? ` · ${fmtWhen(at)}` : ""}</span></div>;
}
export function DateSep({ label }) {
  return <div className="cv-sep">{label}</div>;
}

export function KeyHints({ items }) {
  return (
    <div className="cv-hide-sm" style={{ display: "flex", gap: 18, justifyContent: "center", fontSize: 12, padding: "10px 0 2px", flexWrap: "wrap", color: "var(--t-text-faint)" }}>
      {items.map(([keys, label]) => (
        <span key={label}>{keys.map(k => <span key={k}><span className="cv-kbd">{k}</span> </span>)}{label}</span>
      ))}
    </div>
  );
}

// Floating "Latest" button: shows once you have scrolled up away from the newest message.
export function JumpLatest({ scrollRef, bump }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setShow(el.scrollHeight - el.scrollTop - el.clientHeight > 360);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef, bump]);
  if (!show) return null;
  return (
    <button type="button" className="cv-btn cv-btn-primary" style={{ position: "absolute", right: 28, bottom: 16, borderRadius: 999, zIndex: 5 }}
      onClick={() => { const el = scrollRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }}>
      <ArrowDown size={15} /> Latest
    </button>
  );
}

// Lays out a filtered entry list with date separators, rows/cards and event lines.
// Memoised: typing in the reply box must never redraw the conversation (Juwan 2026-10-27). A
// keystroke used to rebuild every message (~170-320 ms a letter on a 59-reply ticket) and, with
// broken pictures in an email, sent a request per letter. Pass it only stable props (useMemo /
// useCallback / module-level functions), or every keystroke reaches every message again.
// `entries` are pre-sorted oldest → newest. Kinds: client | agent | ai | note |
// activity (expandable) | event (one line).
export const Timeline = memo(function Timeline({ entries, expanded, toggle, focusedId, refFor, onReply, renderQuoted, showDates = true }) {
  let lastDay = null;
  const out = [];
  for (const e of entries) {
    if (showDates && e.at) {
      const key = new Date(e.at).toDateString();
      if (key !== lastDay) { out.push(<DateSep key={`d-${key}`} label={dayLabel(e.at)} />); lastDay = key; }
    }
    if (e.kind === "event") { out.push(<EventLine key={e.id} text={e.text} at={e.at} />); continue; }
    const open = expanded.has(e.id);
    out.push(open
      ? <MessageCard key={e.id} e={e} focused={focusedId === e.id} innerRef={refFor(e.id)} onToggle={() => toggle(e.id)}
          onReply={onReply && e.kind !== "activity" ? () => onReply(e) : undefined} renderQuoted={renderQuoted} />
      : <MessageRow key={e.id} e={e} focused={focusedId === e.id} innerRef={refFor(e.id)} onToggle={() => toggle(e.id)} />);
  }
  return out;
});
